/**
 * Scout v0 — Layer 2 class template picker + inline create/edit.
 *
 * The operator picks an existing class template OR creates/edits one inline.
 * Each template carries roles with a per-role exact/loose toggle. Creating /
 * editing mutates the template via the CRUD API (POST/PUT); the list is
 * optimistically updated by the query hooks.
 */

import { useState } from 'react';
import type { ClassTemplate, MatchMode, RoleSpec } from '@/types/scout';
import { useToast } from '@/components/ui/useToast';
import {
  useArchiveScoutTemplate,
  useCreateScoutTemplate,
  useScoutTemplates,
  useUpdateScoutTemplate,
} from '@/lib/scout-queries';
import { EmptyState, ErrorBanner, LoadingSpinner } from '@/components/ui';
import { getErrorMessage } from '@/lib/queries';

interface TemplatePickerProps {
  /** The currently selected template id (null = none selected). */
  selectedId: string | null;
  onSelect: (template: ClassTemplate | null) => void;
}

export function TemplatePicker({ selectedId, onSelect }: TemplatePickerProps) {
  const { data: templates, isLoading, error, refetch } = useScoutTemplates();
  const createTemplate = useCreateScoutTemplate();
  const updateTemplate = useUpdateScoutTemplate();
  const archiveTemplate = useArchiveScoutTemplate();
  const { addToast } = useToast();

  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState<ClassTemplate | null>(null);

  const sorted = [...(templates ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  const handleSelect = (template: ClassTemplate) => {
    onSelect(template.id === selectedId ? null : template);
  };

  const handleCreateNew = () => {
    setEditing(null);
    setShowEditor(true);
  };

  const handleEdit = (template: ClassTemplate) => {
    setEditing(template);
    setShowEditor(true);
  };

  const handleArchive = async (template: ClassTemplate) => {
    if (!window.confirm(`Archive template "${template.name}"? It can no longer compose new runs.`)) {
      return;
    }
    archiveTemplate.mutate(template.id, {
      onSuccess: () => {
        addToast('success', 'Template archived.');
        if (selectedId === template.id) onSelect(null);
      },
      onError: (err) => addToast('error', getErrorMessage(err)),
    });
  };

  if (error) {
    return (
      <section className="card" aria-labelledby="layer2-heading">
        <h2 id="layer2-heading" className="text-lg font-semibold text-text mb-3">
          Layer 2 - class template
        </h2>
        <ErrorBanner
          message={getErrorMessage(error)}
          retryable={error.retryable}
          onRetry={() => refetch()}
        />
      </section>
    );
  }

  return (
    <section className="card" aria-labelledby="layer2-heading">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 id="layer2-heading" className="text-lg font-semibold text-text">
            Layer 2 - class template
          </h2>
          <p className="text-xs text-muted mt-0.5">Reusable role bundle (e.g. staffing-firm: VA/EA)</p>
        </div>
        <button type="button" className="btn-primary" onClick={handleCreateNew}>
          New template
        </button>
      </div>

      {isLoading ? (
        <LoadingSpinner className="py-6" />
      ) : sorted.length === 0 && !showEditor ? (
        <EmptyState
          title="No class templates yet"
          description="Create a reusable role bundle to compose a run."
          action={{ label: 'Create template', onClick: handleCreateNew }}
        />
      ) : (
        <ul className="space-y-2">
          {sorted.map((template) => (
            <li key={template.id}>
              <TemplateRow
                template={template}
                isSelected={template.id === selectedId}
                onSelect={() => handleSelect(template)}
                onEdit={() => handleEdit(template)}
                onArchive={() => handleArchive(template)}
              />
            </li>
          ))}
        </ul>
      )}

      {showEditor && (
        <TemplateEditor
          // Remount when switching which template is being edited so the form
          // state re-initializes from the new template.
          key={editing?.id ?? 'new'}
          template={editing}
          onClose={() => setShowEditor(false)}
          onSaved={(saved) => {
            setShowEditor(false);
            onSelect(saved);
            addToast('success', editing ? 'Template updated.' : 'Template created.');
          }}
          createMutation={createTemplate}
          updateMutation={updateTemplate}
          onError={(msg: string) => addToast('error', msg)}
        />
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Template row                                                            */
/* ─────────────────────────────────────────────────────────────────────── */

interface TemplateRowProps {
  template: ClassTemplate;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onArchive: () => void;
}

function TemplateRow({ template, isSelected, onSelect, onEdit, onArchive }: TemplateRowProps) {
  const looseCount = template.roles.filter((r) => r.matchMode === 'loose').length;
  return (
    <div
      className={`border rounded-lg p-3 transition-colors duration-fast ${
        isSelected ? 'border-primary bg-primary/5' : 'border-border hover:bg-surface/50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onSelect}
          className="flex-1 text-left min-w-0"
          aria-pressed={isSelected}
        >
          <div className="flex items-center gap-2">
            <span className="font-medium text-text truncate">{template.name}</span>
            {isSelected && (
              <span className="text-primary" aria-hidden="true">
                ✓
              </span>
            )}
          </div>
          <p className="text-xs text-muted mt-0.5 truncate">
            {template.roles.length} role{template.roles.length === 1 ? '' : 's'}
            {looseCount > 0 && ` · ${looseCount} loose`}
            {template.keywords.length > 0 &&
              ` · ${template.keywords.length} keyword${template.keywords.length === 1 ? '' : 's'}`}
            {template.archetype ? ` · ${template.archetype}` : ''}
          </p>
        </button>
        <div className="flex gap-1 shrink-0">
          <button type="button" className="btn-ghost px-2 py-1 text-sm" onClick={onEdit}>
            Edit
          </button>
          <button
            type="button"
            className="btn-ghost px-2 py-1 text-sm text-warning"
            onClick={onArchive}
          >
            Archive
          </button>
        </div>
      </div>

      {isSelected && template.roles.length > 0 && (
        <ul className="mt-2 space-y-1 pl-1">
          {template.roles.map((role) => (
            <li key={role.title} className="text-xs text-muted flex items-center gap-1.5">
              <span
                className={`badge ${role.matchMode === 'loose' ? 'badge-secondary' : 'badge-primary'}`}
              >
                {role.matchMode}
              </span>
              <span className="text-text">{role.title}</span>
              {role.synonyms && role.synonyms.length > 0 && (
                <span className="text-muted">+{role.synonyms.length} synonym(s)</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {isSelected && template.keywords.length > 0 && (
        <p className="mt-2 pl-1 text-xs text-muted">
          Keywords: <span className="text-text">{template.keywords.join(', ')}</span>
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Inline template editor (create / edit)                                 */
/* ─────────────────────────────────────────────────────────────────────── */

interface TemplateEditorProps {
  template: ClassTemplate | null;
  onClose: () => void;
  onSaved: (saved: ClassTemplate) => void;
  createMutation: ReturnType<typeof useCreateScoutTemplate>;
  updateMutation: ReturnType<typeof useUpdateScoutTemplate>;
  onError: (msg: string) => void;
}

interface EditorRoleState {
  title: string;
  matchMode: MatchMode;
}

function TemplateEditor({
  template,
  onClose,
  onSaved,
  createMutation,
  updateMutation,
  onError,
}: TemplateEditorProps) {
  const [name, setName] = useState(template?.name ?? '');
  const [archetype, setArchetype] = useState(template?.archetype ?? '');
  const [roles, setRoles] = useState<EditorRoleState[]>(
    template ? template.roles.map((r) => ({ title: r.title, matchMode: r.matchMode })) : [],
  );
  const [keywords, setKeywords] = useState(
    template ? template.keywords.join(', ') : '',
  );

  const addRole = () => setRoles((prev) => [...prev, { title: '', matchMode: 'exact' }]);
  const removeRole = (idx: number) =>
    setRoles((prev) => prev.filter((_, i) => i !== idx));
  const updateRole = (idx: number, patch: Partial<EditorRoleState>) =>
    setRoles((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const handleSave = (event: React.FormEvent) => {
    event.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      onError('Template name is required.');
      return;
    }

    const roleSpecs: RoleSpec[] = roles
      .filter((r) => r.title.trim())
      .map((r) => ({ title: r.title.trim(), matchMode: r.matchMode }));

    const keywordList = keywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    const payload = { name: trimmedName, archetype: archetype.trim(), roles: roleSpecs, keywords: keywordList };

    if (template) {
      updateMutation.mutate(
        { templateId: template.id, ...payload },
        {
          onSuccess: (updated) => onSaved(updated),
          onError: (err) => onError(getErrorMessage(err)),
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => onSaved(created),
        onError: (err) => onError(getErrorMessage(err)),
      });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <form
      className="mt-4 border border-border rounded-lg p-4 space-y-3 bg-bg/50"
      onSubmit={handleSave}
      aria-label={template ? `Edit template ${template.name}` : 'Create new template'}
    >
      <h3 className="font-semibold text-text">{template ? 'Edit template' : 'New template'}</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs text-muted">Name *</span>
          <input
            className="input mt-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. staffing-firm admin"
            required
            autoFocus
          />
        </label>
        <label className="block">
          <span className="text-xs text-muted">Archetype</span>
          <input
            className="input mt-1"
            value={archetype}
            onChange={(e) => setArchetype(e.target.value)}
            placeholder="e.g. staffing-firm"
          />
        </label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted">Roles</span>
          <button type="button" className="btn-ghost px-2 py-1 text-sm" onClick={addRole}>
            + Add role
          </button>
        </div>
        <ul className="space-y-2">
          {roles.map((role, idx) => (
            <li key={idx} className="flex items-center gap-2">
              <input
                className="input flex-1"
                value={role.title}
                onChange={(e) => updateRole(idx, { title: e.target.value })}
                placeholder="Role title (e.g. Virtual Assistant)"
              />
              <div
                className="flex rounded-md border border-border overflow-hidden shrink-0"
                role="group"
                aria-label={`Match mode for ${role.title || 'role'}`}
              >
                <button
                  type="button"
                  className={`px-3 py-2 text-sm transition-colors duration-fast ${
                    role.matchMode === 'exact'
                      ? 'bg-primary text-bg'
                      : 'bg-bg text-muted hover:text-text'
                  }`}
                  aria-pressed={role.matchMode === 'exact'}
                  onClick={() => updateRole(idx, { matchMode: 'exact' })}
                >
                  exact
                </button>
                <button
                  type="button"
                  className={`px-3 py-2 text-sm transition-colors duration-fast ${
                    role.matchMode === 'loose'
                      ? 'bg-secondary text-white'
                      : 'bg-bg text-muted hover:text-text'
                  }`}
                  aria-pressed={role.matchMode === 'loose'}
                  onClick={() => updateRole(idx, { matchMode: 'loose' })}
                >
                  loose
                </button>
              </div>
              <button
                type="button"
                className="btn-ghost px-2 py-1 text-sm text-warning shrink-0"
                onClick={() => removeRole(idx)}
                aria-label={`Remove role ${role.title || idx + 1}`}
              >
                ×
              </button>
            </li>
          ))}
          {roles.length === 0 && (
            <li className="text-sm text-muted italic">No roles added yet.</li>
          )}
        </ul>
        <p className="text-xs text-muted mt-1">
          Exact = used as-is (no AI call). Loose = AI proposes synonyms before launch.
        </p>
      </div>

      <label className="block">
        <span className="text-xs text-muted">
          Keywords (comma-separated) - ride on every run composed from this template
        </span>
        <input
          className="input mt-1"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="e.g. remote, part-time"
        />
      </label>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={isPending}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isPending}>
          {isPending ? 'Saving...' : template ? 'Save changes' : 'Create template'}
        </button>
      </div>
    </form>
  );
}